// src/utils/MobileInterfaceManager.ts

interface DeviceInfo {
  userAgent: string;
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  isKeyboardVisible: boolean;
  orientation: 'landscape' | 'portrait';
}

export class MobileInterfaceManager {
  private viewportHeight: number = window.innerHeight;
  private isKeyboardVisible: boolean = false;
  private resizeHandler: (() => void) | null = null;
  private orientationHandler: (() => void) | null = null;
  private scrollHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private lockScreenHandler: (() => void) | null = null;

  constructor() {
    this.setupMobileHandlers();
    this.setupIOSSpecificHandlers();
    this.setupAndroidSpecificHandlers();
  }

  /**
   * Setup mobile-specific event handlers
   */
  private setupMobileHandlers(): void {
    // Viewport height detection for mobile keyboards
    this.resizeHandler = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = this.viewportHeight - currentHeight;
      
      // Keyboard threshold (usually keyboard takes 300+ pixels)
      if (heightDifference > 150) {
        this.handleKeyboardShow();
      } else {
        this.handleKeyboardHide();
      }
    };

    // Orientation change handler
    this.orientationHandler = () => {
      setTimeout(() => {
        this.adjustForOrientation();
        this.fixMessageBubbleWidths();
      }, 300); // Wait for orientation change to complete
    };

    // Scroll handling for mobile
    this.scrollHandler = () => {
      this.handleMobileScroll();
    };

    // Page visibility for lock screen detection
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.handleScreenLock();
      } else {
        this.handleScreenUnlock();
      }
    };

    // Add event listeners
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.orientationHandler);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Setup iOS-specific handlers
   */
  private setupIOSSpecificHandlers(): void {
    if (!this.isIOS()) return;

    // iOS viewport fix
    const setViewportHeight = () => {
      document.documentElement.style.setProperty(
        '--viewport-height', 
        `${window.innerHeight}px`
      );
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);

    // iOS keyboard blur handling
    document.addEventListener('focusout', () => {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    });
  }

  /**
   * Setup Android-specific handlers
   */
  private setupAndroidSpecificHandlers(): void {
    if (!this.isAndroid()) return;

    // Android keyboard detection
    let initialViewportHeight = window.innerHeight;
    
    const androidResizeHandler = () => {
      const currentHeight = window.innerHeight;
      const diff = initialViewportHeight - currentHeight;
      
      if (diff > 150) {
        document.body.classList.add('keyboard-open');
      } else {
        document.body.classList.remove('keyboard-open');
      }
    };

    window.addEventListener('resize', androidResizeHandler);
  }

  /**
   * Handle keyboard show
   */
  private handleKeyboardShow(): void {
    if (this.isKeyboardVisible) return;
    
    this.isKeyboardVisible = true;
    document.body.classList.add('mobile-keyboard-visible');
    
    // Scroll to keep input visible
    setTimeout(() => {
      const inputElement = document.querySelector('.message-input');
      if (inputElement) {
        inputElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);

    console.log('⌨️ Mobile keyboard visible');
  }

  /**
   * Handle keyboard hide
   */
  private handleKeyboardHide(): void {
    if (!this.isKeyboardVisible) return;
    
    this.isKeyboardVisible = false;
    document.body.classList.remove('mobile-keyboard-visible');
    
    // Reset viewport
    setTimeout(() => {
      this.viewportHeight = window.innerHeight;
    }, 300);

    console.log('⌨️ Mobile keyboard hidden');
  }

  /**
   * Fix message bubble widths for mobile
   */
  public fixMessageBubbleWidths(): void {
    if (!this.isMobile()) return;

    const messageBubbles = document.querySelectorAll('.message-bubble');
    messageBubbles.forEach((bubble: Element) => {
      const htmlBubble = bubble as HTMLElement;
      const sender = htmlBubble.classList.contains('sender-user') ? 'user' : 'emcog';
      
      if (sender === 'user') {
        // User messages should extend full width on mobile
        htmlBubble.style.maxWidth = '85%';
        htmlBubble.style.marginLeft = '15%';
        htmlBubble.style.marginRight = '0';
      } else {
        // AI messages should also extend properly on mobile
        htmlBubble.style.maxWidth = '85%';
        htmlBubble.style.marginLeft = '0';
        htmlBubble.style.marginRight = '15%';
      }
    });

    // Apply mobile-specific styling
    document.body.classList.add('mobile-interface');
  }

  /**
   * Handle mobile scroll behavior
   */
  private handleMobileScroll(): void {
    // Prevent scroll when keyboard is visible
    if (this.isKeyboardVisible) {
      return;
    }

    // Auto-hide mobile browser UI
    if (this.isMobile()) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > 50) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    }
  }

  /**
   * Adjust interface for orientation changes
   */
  private adjustForOrientation(): void {
    const isLandscape = window.innerWidth > window.innerHeight;
    
    if (isLandscape) {
      document.body.classList.add('landscape-mode');
      document.body.classList.remove('portrait-mode');
    } else {
      document.body.classList.add('portrait-mode');
      document.body.classList.remove('landscape-mode');
    }

    // Fix message bubble widths after orientation change
    this.fixMessageBubbleWidths();
    
    console.log(`📱 Orientation changed: ${isLandscape ? 'landscape' : 'portrait'}`);
  }

  /**
   * Handle screen lock (affects streaming)
   */
  private handleScreenLock(): void {
    // Pause any active streaming when screen locks
    const streamingElements = document.querySelectorAll('.message-bubble.streaming');
    streamingElements.forEach(element => {
      element.classList.add('streaming-paused');
    });

    console.log('🔒 Screen locked - pausing streaming');
  }

  /**
   * Handle screen unlock
   */
  private handleScreenUnlock(): void {
    // Resume streaming when screen unlocks
    const pausedElements = document.querySelectorAll('.message-bubble.streaming-paused');
    pausedElements.forEach(element => {
      element.classList.remove('streaming-paused');
    });

    console.log('🔓 Screen unlocked - resuming streaming');
  }

  /**
   * Get device info for debugging
   */
  public getDeviceInfo(): DeviceInfo {
    return {
      userAgent: navigator.userAgent,
      isMobile: this.isMobile(),
      isIOS: this.isIOS(),
      isAndroid: this.isAndroid(),
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      isKeyboardVisible: this.isKeyboardVisible,
      orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    };
  }

  /**
   * Apply mobile-specific CSS variables
   */
  public applyMobileCSS(): void {
    if (!this.isMobile()) return;

    const root = document.documentElement;
    
    // Mobile-specific sizing
    root.style.setProperty('--mobile-message-padding', '12px 16px');
    root.style.setProperty('--mobile-message-margin', '8px 12px');
    root.style.setProperty('--mobile-bubble-max-width', '85%');
    root.style.setProperty('--mobile-font-size', '16px'); // Prevents zoom on iOS
    
    // Touch-friendly sizing
    root.style.setProperty('--mobile-touch-target', '44px');
    root.style.setProperty('--mobile-input-height', '48px');
    
    // Viewport handling
    root.style.setProperty('--mobile-viewport-height', `${window.innerHeight}px`);
    
    console.log('📱 Mobile CSS variables applied');
  }

  /**
   * Optimize touch interactions
   */
  public optimizeTouchInteractions(): void {
    if (!this.isMobile()) return;

    // Add touch-friendly classes
    document.body.classList.add('touch-device');
  
    // Disable text selection on non-content elements
    const nonContentElements = document.querySelectorAll(
      '.sidebar, .input-area, .message-actions, .status-indicator'
    );
  
    nonContentElements.forEach(element => {
      (element as HTMLElement).style.userSelect = 'none';
      (element as HTMLElement).style.webkitUserSelect = 'none';
    });

    // Enable momentum scrolling with proper type handling
    const scrollableElements = document.querySelectorAll(
      '.chat-container, .sidebar-content, .message-list'
    );
  
    scrollableElements.forEach(element => {
      const htmlElement = element as HTMLElement;
      // Use setProperty to avoid TypeScript issues with webkit-specific properties
      htmlElement.style.setProperty('-webkit-overflow-scrolling', 'touch');
    });
  }

  /**
   * Device detection helpers
   */
  private isMobile(): boolean {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }

  /**
   * Force message bubble width recalculation
   */
  public recalculateMessageWidths(): void {
    // Trigger a reflow to recalculate widths
    requestAnimationFrame(() => {
      this.fixMessageBubbleWidths();
      
      // Force layout recalculation
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        const currentDisplay = (chatContainer as HTMLElement).style.display;
        (chatContainer as HTMLElement).style.display = 'none';
        // FIXED: Explicitly void the expression to avoid ESLint error
        void (chatContainer as HTMLElement).offsetHeight; // Trigger reflow
        (chatContainer as HTMLElement).style.display = currentDisplay || '';
      }
    });
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    if (this.orientationHandler) {
      window.removeEventListener('orientationchange', this.orientationHandler);
    }
    
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    // Remove mobile classes
    document.body.classList.remove(
      'mobile-interface', 
      'touch-device', 
      'mobile-keyboard-visible',
      'landscape-mode',
      'portrait-mode'
    );
  }
}